if (!customElements.get('tridoo-coordinati')) {
  const catalogs = new Map();
  function readCatalog(url) {
    if (!catalogs.has(url)) {
      catalogs.set(url, fetch(url).then((response) => {
        if (!response.ok) throw new Error('catalog');
        return response.json();
      }).then((data) => {
        if (!Array.isArray(data.products)) throw new Error('catalog');
        return data;
      }).catch((error) => { catalogs.delete(url); throw error; }));
    }
    return catalogs.get(url);
  }

  customElements.define('tridoo-coordinati', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.primary = JSON.parse(this.querySelector('[data-primary]').textContent);
      this.companions = JSON.parse(this.querySelector('[data-companions]').textContent);
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.cache = new Map();
      this.revision = 0;
      this.form = this.querySelector('[data-companion-form]');
      this.products = this.querySelector('[data-product]');
      this.variants = this.querySelector('[data-variant]');
      this.nameInput = this.querySelector('[data-name]');
      this.addButton = this.querySelector('[data-add]');
      this.status = this.querySelector('[data-status]');
      this.cardStatus = this.querySelector('[data-card-status]');
      this.image = this.querySelector('[data-image]');
      this.configurator = this.querySelector('[data-configurator]');
      this.retry = this.querySelector('[data-retry]');
      this.personalLink = this.querySelector('[data-personal-link]');
      this.primaryInfo = document.querySelector(`product-info[data-product-id="${this.dataset.productId}"]`);
      this.primaryForm = this.primaryInfo?.querySelector('form[data-type="add-to-cart-form"]');
      this.onPrimaryChange = () => this.primaryChanged();
      this.onProductChange = () => this.loadProduct();
      this.onVariantChange = () => this.updateSummary();
      this.onSubmit = (event) => this.add(event);
      this.onPersonalClick = () => this.updatePersonal();
      this.personalLink?.addEventListener('click', this.onPersonalClick);
      this.onRetry = () => { this.catalogPromise = null; this.loadProduct(); };
      this.products?.addEventListener('change', this.onProductChange);
      this.variants?.addEventListener('change', this.onVariantChange);
      this.form?.addEventListener('submit', this.onSubmit);
      this.retry?.addEventListener('click', this.onRetry);
      document.addEventListener('variant:changed', this.onPrimaryChange);
      if (window.FoxTheme?.pubsub?.subscribe) {
        this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.primaryInfo?.dataset.section) this.primaryChanged();
        });
      }
      if (this.primaryForm) {
        this.observer = new MutationObserver(this.onPrimaryChange);
        this.observer.observe(this.primaryForm, { attributes: true, subtree: true, attributeFilter: ['disabled', 'value'] });
        this.primaryForm.addEventListener('input', this.onPrimaryChange);
      }
      this.loadProduct();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onPrimaryChange);
      this.personalLink?.removeEventListener('click', this.onPersonalClick);
      this.primaryForm?.removeEventListener('input', this.onPrimaryChange);
      this.observer?.disconnect();
      this.unsubscribe?.();
      this.products?.removeEventListener('change', this.onProductChange);
      this.variants?.removeEventListener('change', this.onVariantChange);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.retry?.removeEventListener('click', this.onRetry);
      this.initialized = false;
      this.revision++;
    }

    get selectedPrimary() {
      const id = this.primaryForm?.elements.namedItem('id')?.value;
      return this.primary.variants.find((variant) => String(variant.id) === String(id));
    }

    get primaryReady() {
      const button = this.primaryForm?.querySelector('[name="add"]');
      return Boolean(this.selectedPrimary?.available && button && !button.disabled);
    }

    get primaryCatalog() {
      return this.catalog?.products.find((product) => String(product.id) === this.dataset.productId);
    }

    get colorKey() {
      return this.primaryCatalog?.variants.find((variant) => String(variant.id) === String(this.selectedPrimary?.id))?.colorKey;
    }

    primaryChanged() {
      if (!this.catalog) { this.loadProduct(); return; }
      if (!this.form) { this.updatePersonal(); return; }
      this.refresh();
      const handle = this.products.value;
      const entry = this.prepareModels();
      if (!entry) { this.companion = null; this.refresh(); return; }
      if (entry.handle === handle && this.companion) this.refresh();
      else this.loadProduct();
    }

    async getCatalog() {
      if (!this.catalogPromise) {
        const liveUrl = 'https://ilkuebbvrbkudpinzfsi.supabase.co/functions/v1/kibbu-catalogo';
        this.catalogPromise = this.dataset.previewCatalog && this.dataset.preview === 'true'
          ? Promise.all([readCatalog(liveUrl).catch(() => null), readCatalog(this.dataset.previewCatalog)]).then(([live, preview]) => {
              if (preview.preview_only !== true) throw new Error('preview');
              const merged = new Map(preview.products.map((product) => [String(product.id), product]));
              for (const product of live?.products || []) merged.set(String(product.id), { ...product, status: 'ACTIVE' });
              return { currency: live?.currency || preview.currency, products: [...merged.values()] };
            })
          : readCatalog(liveUrl);
      }
      return this.catalogPromise;
    }

    catalogProduct(product) {
      if (!product?.variants?.length || product.variants.some((variant) => !variant.options || Array.isArray(variant.options))) return null;
      const names = [...new Set(product.variants.flatMap((variant) => Object.keys(variant.options)))];
      return {
        ...product, options: names.map((name) => ({ name })), featured_image: product.image,
        variants: product.variants.map((variant) => ({ ...variant, options: names.map((name) => variant.options[name] || ''), featured_image: variant.image ? { src: variant.image } : null }))
      };
    }

    prepareModels() {
      const selected = this.products.value;
      const allowed = this.companions.map((product) => product.handle).filter(Boolean);
      const target = this.dataset.direction === 'bowl' ? 'portasacchetti' : 'ciotola';
      const candidates = this.catalog.products.filter((product) => {
        if (allowed.length ? !allowed.includes(product.handle) : product.kind !== target) return false;
        return this.colorKey && product.variants.some((variant) => variant.colorKey === this.colorKey && variant.available);
      });
      const preferred = target === 'ciotola' ? 'rea-portaciotola-personalizzato' : 'slim-lite-portasacchetti-cane-testa-razza';
      candidates.sort((a, b) => {
        const rank = (product) => allowed.length ? allowed.indexOf(product.handle) : product.handle === preferred ? -1 : 0;
        return rank(a) - rank(b);
      });
      this.products.replaceChildren();
      for (const product of candidates) {
        const label = product.title?.replace(/\s+(?:portaciotola|portasacchetti).*$/i, '') || product.handle;
        this.products.add(new Option(label, product.handle));
      }
      this.products.disabled = !candidates.length;
      if (candidates.some((product) => product.handle === selected)) this.products.value = selected;
      return candidates.find((product) => product.handle === this.products.value) || null;
    }

    async loadProduct() {
      const revision = ++this.revision;
      this.companion = null;
      if (this.addButton) this.addButton.disabled = true;
      if (this.variants) this.variants.disabled = true;
      this.setStatus(this.messages.loading);
      if (this.retry) this.retry.hidden = true;
      try {
        this.catalog = await this.getCatalog();
        if (revision !== this.revision || !this.isConnected) return;
        if (this.catalog.currency && this.catalog.currency !== (this.dataset.currency || 'EUR')) throw new Error('currency');
        this.updatePersonal();
        if (!this.form) return;
        const entry = this.prepareModels();
        if (!entry) { this.refresh(); return; }
        const handle = entry.handle;
        let product = this.catalogProduct(entry) || this.cache.get(handle);
        if (!product) {
          const root = this.dataset.root.endsWith('/') ? this.dataset.root : `${this.dataset.root}/`;
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('catalog');
          product = await response.json();
          if (!Array.isArray(product.variants) || product.variants.length >= 250) throw new Error('variants');
          this.cache.set(handle, product);
        }
        if (revision !== this.revision || !this.isConnected) return;
        this.companion = product;
        const config = entry.personalization || this.companions.find((item) => item.handle === handle)?.personalization;
        const enabled = config?.enabled === true || config?.attiva === true;
        this.querySelector('[data-personalization]').hidden = !enabled;
        this.nameInput.disabled = !enabled;
        this.nameInput.required = enabled && (config.required === true || config.obbligatorio === true);
        this.nameInput.maxLength = Number(config?.maxLength || config?.max_caratteri) || 12;
        const nameLabel = config?.label || config?.etichetta || this.messages.name;
        this.nameInput.name = `properties[${nameLabel}]`;
        this.querySelector('[data-name-label]').textContent = nameLabel;
        this.refresh();
      } catch {
        if (revision !== this.revision) return;
        this.catalogPromise = null;
        this.setStatus(this.messages.error);
        if (this.configurator) this.configurator.hidden = true;
        if (this.retry) this.retry.hidden = false;
        this.personalStatus(this.messages.error);
      }
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
      if (this.cardStatus) this.cardStatus.textContent = message;
    }

    refresh() {
      if (!this.form) { this.updatePersonal(); return; }
      const previous = this.variants.value;
      this.variants.replaceChildren();
      const colorIndex = this.primary.options.indexOf('Colorazione');
      const color = this.selectedPrimary?.options[colorIndex];
      const companionColorIndex = this.companion?.options.findIndex((option) => (option.name || option) === 'Colorazione');
      const companionKeys = this.catalog?.products.find((product) => product.handle === this.products.value)?.variants || [];
      const matches = color && companionColorIndex >= 0 && this.colorKey
        ? this.companion.variants.filter((variant) => companionKeys.some((entry) => String(entry.id) === String(variant.id) && entry.colorKey === this.colorKey && entry.available))
        : [];
      for (const variant of matches) {
        const label = variant.options.filter((_, index) => index !== companionColorIndex).join(' / ') || color;
        const option = new Option(`${label}${variant.available ? '' : ` · ${this.messages.sold_out}`}`, variant.id);
        option.disabled = !variant.available;
        this.variants.add(option);
      }
      const selectionMatches = (variant) => this.companion.options.every((option, index) => {
        const name = option.name || option;
        return name === 'Colorazione' || this.companionSelection?.[name] === variant.options[index];
      });
      const selected = matches.find((variant) => String(variant.id) === previous && variant.available)
        || matches.find((variant) => variant.available && selectionMatches(variant))
        || matches.find((variant) => variant.available);
      if (selected) this.variants.value = String(selected.id);
      this.variants.disabled = !selected;
      const colorText = color ? `${this.messages.color} ${color}` : '';
      this.querySelector('[data-color]').textContent = colorText;
      const cardColor = this.querySelector('[data-card-color]');
      if (cardColor) cardColor.textContent = selected ? colorText : '';
      this.setStatus(selected ? '' : this.messages.unavailable);
      if (this.configurator) this.configurator.hidden = !selected;
      this.updateSummary();
      this.updatePersonal();
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
    }

    imageUrl(source) {
      const url = new URL(source, window.location.href);
      if (url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/')) url.searchParams.set('width', '420');
      return url.href;
    }

    updateSummary() {
      this.selectedCompanion = this.companion?.variants.find((variant) => String(variant.id) === this.variants.value);
      const primary = this.selectedPrimary;
      const companion = this.selectedCompanion;
      if (companion) {
        this.companionSelection = Object.fromEntries(this.companion.options.map((option, index) => [option.name || option, companion.options[index]])
          .filter(([name]) => name !== 'Colorazione'));
      }
      const ready = this.primaryReady && companion?.available && !this.variants.disabled;
      this.previewBlocked = this.dataset.preview === 'true' && (this.primaryCatalog?.status === 'DRAFT' || this.companion?.status === 'DRAFT');
      this.addButton.disabled = this.busy || !ready || this.previewBlocked;
      const note = this.querySelector('[data-preview-note]');
      if (note) note.hidden = !this.previewBlocked;
      const total = this.querySelector('[data-total]');
      total.textContent = '';
      if (primary && companion) {
        const quantity = Number(this.primaryForm.elements.namedItem('quantity')?.value || 1);
        total.textContent = `${this.messages.total} ${this.money(Math.round(primary.price * quantity * 0.8) + Math.round(companion.price * quantity * 0.8))}`;
      }
      const source = companion?.featured_image?.src || (typeof companion?.featured_image === 'string' ? companion.featured_image : null) || this.companion?.featured_image;
      this.image.hidden = !source || !companion;
      if (source && companion) {
        this.image.src = this.imageUrl(source);
        this.image.alt = `${this.companion.title} · ${companion.title}`;
      }
      const model = this.querySelector('[data-card-model]');
      if (model) model.textContent = companion ? this.companion.title.replace(/\s+(?:portaciotola|portasacchetti).*$/i, '') : '';
    }

    personalStatus(message) {
      const status = this.querySelector('[data-personal-status]');
      if (status) status.textContent = message || '';
    }

    updatePersonal() {
      if (!this.personalLink) return;
      const product = this.catalog?.products.find((item) => item.kind === 'personalizzato');
      const candidates = product?.variants.filter((variant) => variant.available) || [];
      const base = this.primaryCatalog?.handle?.startsWith('clessidra-') ? 'Clessidra' : this.primaryCatalog?.handle?.startsWith('slim-portasacchetti') ? 'Slim' : 'Slim Lite';
      const variant = candidates.find((item) => item.colorKey === this.colorKey && item.options.Base === base) || candidates.find((item) => item.options.Base === base) || candidates[0];
      const price = this.querySelector('[data-personal-price]');
      if (price) price.textContent = variant ? `${this.messages.personal_price} · ${this.money(variant.price)}` : '';
      const image = this.querySelector('[data-personal-image]');
      if (image && image.dataset.fixedImage !== 'true') {
        const source = variant?.image || product?.image;
        image.hidden = !source;
        if (source) { image.src = this.imageUrl(source); image.alt = this.messages.personal_example || product.title; }
      }
      const url = new URL(this.dataset.personalizedUrl || this.personalLink.href, window.location.href);
      if (!['https:', 'http:'].includes(url.protocol)) return;
      url.searchParams.set('set', this.dataset.direction === 'bowl' ? 'ciotola' : 'solo');
      const nativePath = url.pathname.endsWith('/products/portasacchetti-personalizzato-testa-del-tuo-cane')
        || (this.dataset.preview === 'true' && url.pathname === '/products_preview' && url.searchParams.has('preview_key'));
      if (url.origin === location.origin && nativePath && variant) {
        url.searchParams.set('variant', variant.id);
        const theme = new URL(location.href).searchParams.get('preview_theme_id');
        if (theme) url.searchParams.set('preview_theme_id', theme);
        if (this.dataset.direction === 'bowl' && this.selectedPrimary) {
          url.searchParams.set('ciotola', this.selectedPrimary.id);
          const name = Object.values(this.properties(this.primaryForm))[0];
          if (name) url.searchParams.set('nome', name); else url.searchParams.delete('nome');
        } else { url.searchParams.delete('ciotola'); url.searchParams.delete('nome'); }
      }
      this.personalLink.href = url.href;
      this.personalStatus(variant ? '' : this.messages.personal_unavailable);
    }

    properties(form) {
      const properties = {};
      for (const [key, value] of new FormData(form)) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value.trim()) properties[match[1]] = value.trim();
      }
      return properties;
    }

    validate(form) {
      for (const input of form.elements) {
        if (input.name?.startsWith('properties[') && ['text', 'textarea'].includes(input.type)) input.value = input.value.trim();
      }
      return form.reportValidity();
    }

    async add(event) {
      event.preventDefault();
      if (this.busy || this.previewBlocked || !this.primaryReady || !this.selectedCompanion?.available || this.variants.disabled) return;
      if (!this.validate(this.primaryForm) || !this.validate(this.form)) return;
      const quantity = Number(this.primaryForm.elements.namedItem('quantity')?.value || 1);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 20) return;
      const primaryProperties = this.properties(this.primaryForm);
      const nameLabel = this.primary.personalization?.etichetta || this.messages.name;
      const righe = [
        { variant_id: String(this.selectedPrimary.id), quantity, name: primaryProperties[nameLabel] || '' },
        { variant_id: String(this.selectedCompanion.id), quantity, name: this.nameInput.disabled ? '' : this.nameInput.value.trim() }
      ];
      const fingerprint = JSON.stringify(righe);
      if (this.requestFingerprint !== fingerprint) { this.requestId = crypto.randomUUID(); this.requestFingerprint = fingerprint; }
      this.busy = true;
      this.updateSummary();
      this.setStatus(this.messages.loading);
      try {
        const response = await fetch('https://kibbu.it/api/public/testa-cane/coordinato', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ request_id: this.requestId, righe })
        });
        const result = await response.json();
        if (!response.ok || !result.url) throw new Error(result.error || this.messages.error);
        this.setStatus(this.messages.success);
        window.location.assign(result.url);
      } catch (error) {
        this.setStatus(error.message || this.messages.error);
        this.busy = false;
        this.updateSummary();
      }
    }
  });
}
